import { Controller } from "@hotwired/stimulus";

/**
 * Dashboard Demo Controller
 *
 * Animates a mock dashboard showing:
 * 1. A row being "clicked" (highlighted)
 * 2. A drawer sliding in with request details
 * 3. Drawer sliding out after a delay
 * 4. Loop continues with different rows
 */
export default class extends Controller<HTMLElement> {
  static targets = ["row", "drawer", "drawerContent"];

  declare readonly rowTargets: HTMLElement[];
  declare readonly drawerTarget: HTMLElement;
  declare readonly drawerContentTarget: HTMLElement;
  declare readonly hasDrawerTarget: boolean;

  private animationTimeout: ReturnType<typeof setTimeout> | null = null;
  private currentRowIndex: number = 0;
  private observer: IntersectionObserver | null = null;
  private isAnimating: boolean = false;

  connect(): void {
    this.setupIntersectionObserver();
  }

  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isAnimating) {
            this.startAnimation();
          }
        });
      },
      { threshold: 0.3 }
    );

    this.observer.observe(this.element);
  }

  private async startAnimation(): Promise<void> {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Reset state
    this.resetAnimation();

    // Initial delay
    await this.pause(1500);

    // Animation loop
    await this.runAnimationCycle();
  }

  private async runAnimationCycle(): Promise<void> {
    // Always use the first row
    const row = this.rowTargets[0];

    if (!row) {
      this.isAnimating = false;
      return;
    }

    // Highlight the row (click effect)
    row.classList.add("bg-primary/20", "ring-1", "ring-primary/30");

    // Small delay to show the click
    await this.pause(300);

    // Slide in the drawer
    this.openDrawer();

    // Keep drawer open
    await this.pause(4000);

    // Slide out the drawer
    this.closeDrawer();

    // Wait for drawer to close
    await this.pause(500);

    // Remove row highlight
    row.classList.remove("bg-primary/20", "ring-1", "ring-primary/30");

    // Pause before next cycle
    await this.pause(2000);

    // Continue the loop
    this.animationTimeout = setTimeout(() => {
      this.runAnimationCycle();
    }, 100);
  }

  private resetAnimation(): void {
    // Remove all row highlights
    this.rowTargets.forEach((row) => {
      row.classList.remove("bg-primary/20", "ring-1", "ring-primary/30");
    });

    // Close drawer
    this.closeDrawer();
  }

  private openDrawer(): void {
    if (!this.hasDrawerTarget) return;
    this.drawerTarget.classList.remove("translate-x-full");
    this.drawerTarget.classList.add("translate-x-0");
  }

  private closeDrawer(): void {
    if (!this.hasDrawerTarget) return;
    this.drawerTarget.classList.remove("translate-x-0");
    this.drawerTarget.classList.add("translate-x-full");
  }

  private pause(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
